import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { useAuth, useAuthCheck } from '@/hooks/auth';
import { buildSigninPath } from '@/utils/return-to';
import { AuthContext, type AuthContextType } from './auth-context';

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthCheckRateLimitedProps {
  retrying: boolean;
  onRetry: () => void;
}

const AuthCheckRateLimited = ({ retrying, onRetry }: AuthCheckRateLimitedProps) => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4 text-center">
    <p>
      短時間にアクセスが集中したため、ログイン状態を確認できませんでした。
      <br />
      しばらく時間をおいてから再試行してください。
    </p>
    <Button type="button" onClick={onRetry} loading={retrying}>
      再試行
    </Button>
  </div>
);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { data, refetch, isFetching } = useAuthCheck();

  // ログイン画面へ戻すとトークンが残っていても入り直しになるため、ここで止めて確かめ直せるようにする
  if (data.isRateLimited) {
    return <AuthCheckRateLimited retrying={isFetching} onRetry={() => void refetch()} />;
  }
  // 確かめ直して通ったときに認証状態を初期値から作り直すよう、別のコンポーネントにして載せ直す
  return <AuthStateProvider>{children}</AuthStateProvider>;
};

const AuthStateProvider = ({ children }: AuthProviderProps) => {
  const { data } = useAuthCheck();
  const [authInfo, setAuthInfo] = useState<AuthContextType>({
    isAuthenticated: data.isAuthenticated ?? false,
    isAdmin: data.user?.role === 'ADMIN',
    orgId: data.user?.organization?.id ?? 0, // 無効なIDの場合は 0 を設定しておく.(バックエンドでも無効な値)
    featureFlags:
      data.user?.organization?.feature_flags?.map((f) => ({
        featureType: f.feature_type,
        isEnabled: f.is_enabled,
      })) ?? [],
    menuVisibilitySettings:
      data.user?.organization?.menu_visibility_settings?.map((s) => ({
        menuType: s.menu_type,
        isHidden: s.is_hidden,
      })) ?? [],
  });

  useEffect(() => {
    setAuthInfo({
      isAuthenticated: data.isAuthenticated ?? false,
      isAdmin: data.user?.role === 'ADMIN',
      orgId: data.user?.organization?.id ?? 0,
      featureFlags:
        data.user?.organization?.feature_flags?.map((f) => ({
          featureType: f.feature_type,
          isEnabled: f.is_enabled,
        })) ?? [],
      menuVisibilitySettings:
        data.user?.organization?.menu_visibility_settings?.map((s) => ({
          menuType: s.menu_type,
          isHidden: s.is_hidden,
        })) ?? [],
    });
  }, [data]);

  return (
    <AuthContext.Provider
      value={{
        ...authInfo,
        setAuthInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // 通知メールのリンク(?link_to_id= 等)を踏んで未ログインだった場合にクエリを失わないよう
    // search も含めて戻り先に引き継ぐ
    return <Navigate to={buildSigninPath(`${location.pathname}${location.search}`)} replace />;
  }

  return <>{children}</>;
};
